import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import Chat from '@components/Chat';
import LoadingPresenter from '@components/Loading';
import ChatEmptyPresenter from '@components/Chat/ChatEmptyPresenter';
import {
  CapaErrorCode,
  useFetchQuotationUserRfqDetailQuery
} from '@generated/graphql';
import { RootState } from '@modules/index';
import RequestDetail from './RequestDetail';
import PlacingAnOrder from './PlacingAnOrder';
import QuotationDetail from './QuotationDetail';
import RequestDetailEmptyPresenter from './RequestDetail/RequestDetailEmptyPresenter';
import useQueryString from '@hooks/useQueryString';
import { useSnackbar } from 'notistack';
import { useHistory } from 'react-router-dom';

interface RightSideProps {
  matches: boolean;
  chatRoomId: string;
  rfqIdFromParams: string;
  route: 'chat' | 'dashboard';
  onChangePath: (_path: string, callback?: () => void) => void;
}

const RightSide = ({
  route,
  matches,
  rfqIdFromParams,
  chatRoomId,
  onChangePath
}: RightSideProps) => {
  const { query, onReplaceQueryString } = useQueryString<{
    menu?: 'create_purchaseorder';
    selectedQuotationId?: string;
    p?: string;
  }>();
  const history = useHistory();
  const isCreatePurchaseOrder = Boolean(query.menu === 'create_purchaseorder');
  const selectedQuotationId = useSelector(
    (state: RootState) => state.dashboard.selectedQuotationId,
    shallowEqual
  );
  const { enqueueSnackbar } = useSnackbar();

  const [placingAnOrder, setPlacingAnOrder] = useState(false);

  const currentRfqId = rfqIdFromParams || (selectedQuotationId?.rfqId ?? '');

  const { data, loading, refetch } = useFetchQuotationUserRfqDetailQuery({
    variables: {
      rfqId: rfqIdFromParams
    },
    skip: !rfqIdFromParams,
    onCompleted: (response) => {
      if (
        response.fetchQuotationUserRfqDetail?.result ===
        CapaErrorCode.RfqNotFound
      ) {
        enqueueSnackbar('견적요청서를 찾을 수 없습니다.');
        history.push('/dashboard');
      }
    }
  });

  const isQuotation = ['DETAIL', 'SIMPLE', 'NO_QUOTATION'].includes(
    selectedQuotationId?.quotationType as string
  );

  // 견적서 없이 시작한 채팅
  const notIssuedQuotation =
    selectedQuotationId?.quotationType === 'NO_QUOTATION';

  const handleRfqInfoRefetch = useCallback(async () => {
    await refetch();
  }, [refetch]);

  useEffect(() => {
    setPlacingAnOrder(false);
  }, [selectedQuotationId]);

  return (
    <React.Fragment>
      {loading && <LoadingPresenter open />}

      {route === 'dashboard' &&
        (data ? (
          <>
            {/* 요청서 */}
            {!placingAnOrder &&
              !isQuotation &&
              !notIssuedQuotation &&
              !isCreatePurchaseOrder &&
              currentRfqId && (
                <RequestDetail
                  matches={matches}
                  rfqInfo={data}
                  rfqInfoRefetch={handleRfqInfoRefetch}
                  onChangePath={onChangePath}
                />
              )}

            {/* 견적서 */}
            {!placingAnOrder && isQuotation && (
              <QuotationDetail
                rfqId={currentRfqId}
                partnerId={selectedQuotationId?.partnerId}
                quotationId={selectedQuotationId?.quotationId}
                chatRoomId={selectedQuotationId?.chatRoomId}
                quotationType={selectedQuotationId?.quotationType}
                rfqInfo={data}
                matches={matches}
                notIssuedQuotation={notIssuedQuotation}
                onChangePath={onChangePath}
                placingAnOrder={() => {
                  setPlacingAnOrder(true);
                  onReplaceQueryString({ p: '2' });
                }}
              />
            )}

            {((isQuotation && placingAnOrder) || isCreatePurchaseOrder) && (
              <PlacingAnOrder
                onClose={() => {
                  if (placingAnOrder) {
                    setPlacingAnOrder(false);
                  } else {
                    onReplaceQueryString({});
                  }
                }}
                rfqId={currentRfqId}
                partnerId={selectedQuotationId?.partnerId ?? undefined}
                chatRoomId={selectedQuotationId?.chatRoomId ?? undefined}
                quotationId={selectedQuotationId?.quotationId ?? undefined}
              />
            )}
          </>
        ) : (
          <RequestDetailEmptyPresenter />
        ))}

      {route === 'chat' && (chatRoomId ? <Chat /> : <ChatEmptyPresenter />)}
    </React.Fragment>
  );
};

export default RightSide;
